import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  MessageSquare,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  User,
  X,
} from "lucide-react";

import { SEOHead } from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { getOrganizationEvents, getProducerEvents } from "@/lib/api-producer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SearchInput } from "@/components/ui/search-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import {
  panelAccentDot,
  panelControl,
  panelDialog,
  panelEmpty,
  panelEyebrow,
  panelFieldLabel,
  panelPage,
  panelPill,
  panelRow,
  panelSurface,
  panelTitle,
} from "@/lib/panel-ui";
import { cn } from "@/lib/utils";

const QUICK_REPLIES_STORAGE_KEY = "producer:quick-replies";
const URGENCY_OVERRIDES_STORAGE_KEY = "producer:urgent-overrides";

const DEFAULT_QUICK_REPLIES = [
  "Obrigado pela mensagem. Vou validar com a organização e retorno em breve.",
  "Recebemos sua solicitação. Nosso suporte responderá detalhadamente em até 2 horas úteis.",
  "Perfeito, conseguimos ajudar com isso. Por favor, confirme o número do pedido ou e-mail de compra para prosseguirmos.",
  "Confirmamos que seu ingresso está válido e regularizado. Aguardamos você no evento!",
];

const EASE = [0.23, 1, 0.32, 1] as const;

export default function ProducerInbox() {
  const { user, profile } = useAuth();
  const { activeContext } = useOrganization();
  const isOrgContext = activeContext.type === "organization" && !!activeContext.organizationId;
  const queryClient = useQueryClient();
  const { t } = useTranslation("producerMessages");
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();

  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [replyText, setReplyText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "new" | "urgent" | "answered">("all");
  const [activeEventFilter, setActiveEventFilter] = useState<string>("all");

  const [quickReplies, setQuickReplies] = useState<string[]>(DEFAULT_QUICK_REPLIES);
  const [templateDraft, setTemplateDraft] = useState("");
  const [editingTemplateIndex, setEditingTemplateIndex] = useState<number | null>(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);

  const [urgentOverrides, setUrgentOverrides] = useState<Record<string, boolean>>({});
  const [localRepliedIds, setLocalRepliedIds] = useState<Record<string, boolean>>({});

  // Carregar templates e overrides do localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(QUICK_REPLIES_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
          setQuickReplies(parsed.length ? parsed : DEFAULT_QUICK_REPLIES);
        }
      }
    } catch {
      // Ignora payload corrompido
    }

    try {
      const rawUrgency = localStorage.getItem(URGENCY_OVERRIDES_STORAGE_KEY);
      if (rawUrgency) {
        const parsedUrgency = JSON.parse(rawUrgency);
        if (parsedUrgency && typeof parsedUrgency === "object") {
          setUrgentOverrides(parsedUrgency as Record<string, boolean>);
        }
      }
    } catch {
      // Ignora payload corrompido
    }
  }, []);

  // Persistir no localStorage
  useEffect(() => {
    try {
      localStorage.setItem(QUICK_REPLIES_STORAGE_KEY, JSON.stringify(quickReplies));
      localStorage.setItem(URGENCY_OVERRIDES_STORAGE_KEY, JSON.stringify(urgentOverrides));
    } catch {
      // Limite de storage silencioso
    }
  }, [quickReplies, urgentOverrides]);

  // Reseta filtros e seleção quando o produtor troca de organização ou vai para o perfil pessoal
  useEffect(() => {
    setActiveEventFilter("all");
    setSelectedMessage(null);
  }, [activeContext.type, activeContext.organizationId]);

  // Lista de eventos disponíveis no contexto atual (para o seletor)
  const { data: contextEvents = [] } = useQuery({
    queryKey: ["producer-inbox-events", user?.id, activeContext.type, activeContext.organizationId],
    queryFn: async () => {
      if (isOrgContext) {
        return getOrganizationEvents(activeContext.organizationId!);
      }
      return getProducerEvents(user!.id).catch(() => []);
    },
    enabled: isOrgContext ? !!activeContext.organizationId : !!user?.id,
    staleTime: 60_000,
  });

  // Consulta de mensagens do produtor ou da organização ativa
  const { data: messages = [], isLoading, error: inboxError } = useQuery({
    queryKey: ["producer-inbox", user?.id, activeContext.type, activeContext.organizationId],
    queryFn: async () => {
      const baseSelect =
        "id, producer_id, sender_id, sender_name, sender_email, subject, message, is_read, created_at";

      let rows: any[] = [];
      const eventMap = new Map<string, { id: string; title: string; slug: string }>();

      if (isOrgContext) {
        // Carrega os eventos da organização para mapear IDs e nomes
        const orgEvents = await getOrganizationEvents(activeContext.organizationId!).catch(() => []);
        const orgEventIds = orgEvents.map((e: any) => e.id);

        orgEvents.forEach((ev: any) => {
          eventMap.set(ev.id, { id: ev.id, title: ev.title, slug: ev.slug });
        });

        if (orgEventIds.length === 0) {
          return [];
        }

        const { data, error } = await supabase
          .from("producer_messages")
          .select(`${baseSelect}, event_id, replied_at, is_urgent`)
          .in("event_id", orgEventIds)
          .order("created_at", { ascending: false })
          .limit(100);

        if (error) throw error;
        rows = data || [];
      } else {
        // Contexto pessoal: eventos do usuário e mensagens diretas
        const personalEvents = await getProducerEvents(user!.id).catch(() => []);
        const personalEventIds = personalEvents.map((e: any) => e.id);

        personalEvents.forEach((ev: any) => {
          eventMap.set(ev.id, { id: ev.id, title: ev.title, slug: ev.slug });
        });

        let query = supabase
          .from("producer_messages")
          .select(`${baseSelect}, event_id, replied_at, is_urgent`)
          .order("created_at", { ascending: false })
          .limit(100);

        if (personalEventIds.length > 0) {
          query = query.or(`producer_id.eq.${user!.id},event_id.in.(${personalEventIds.join(",")})`);
        } else {
          query = query.eq("producer_id", user!.id);
        }

        const { data, error } = await query;
        if (error) throw error;
        rows = data || [];
      }

      return rows.map((row: any) => ({
        ...row,
        event: row.event_id ? eventMap.get(row.event_id) || null : null,
      }));
    },
    enabled: isOrgContext ? !!activeContext.organizationId : !!user?.id,
    staleTime: 10_000,
  });

  useRealtimeSubscription({
    table: "producer_messages",
    queryKey: ["producer-inbox", user?.id, activeContext.type, activeContext.organizationId],
    enabled: !!user?.id,
  });

  const markAsRead = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from("producer_messages")
        .update({ is_read: true })
        .eq("id", messageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-inbox"] });
    },
  });

  const toggleUrgency = useMutation({
    mutationFn: async ({ messageId, value }: { messageId: string; value: boolean }) => {
      const { error } = await supabase
        .from("producer_messages")
        .update({ is_urgent: value })
        .eq("id", messageId);

      if (error && /is_urgent/i.test(error.message || "")) {
        return { legacy: true };
      }

      if (error) throw error;
      return { legacy: false };
    },
    onSuccess: (result, vars) => {
      if (result.legacy) {
        setUrgentOverrides((prev) => ({ ...prev, [vars.messageId]: vars.value }));
      }
      queryClient.invalidateQueries({ queryKey: ["producer-inbox"] });
      setSelectedMessage((prev: any) =>
        prev?.id === vars.messageId ? { ...prev, is_urgent: vars.value } : prev,
      );
      toast({
        title: vars.value
          ? t("inbox.toasts.markedUrgent")
          : t("inbox.toasts.urgencyRemoved"),
      });
    },
    onError: (err: any) => {
      toast({
        title: t("inbox.toasts.urgencyError"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMessage || !user || !replyText.trim()) return;

      const payload = {
        producer_id: selectedMessage.sender_id,
        sender_id: user.id,
        sender_name: profile?.full_name || "Produtor",
        sender_email: user.email || "",
        event_id: selectedMessage.event_id || null,
        subject: `Re: ${selectedMessage.subject}`,
        message: replyText.trim(),
      };

      let { data: insertedMsg, error: msgErr } = await supabase
        .from("producer_messages")
        .insert(payload)
        .select()
        .single();

      if (msgErr && /event_id/i.test(msgErr.message || "")) {
        const { event_id: _ignored, ...legacyPayload } = payload;
        const legacyInsert = await supabase
          .from("producer_messages")
          .insert(legacyPayload)
          .select()
          .single();
        msgErr = legacyInsert.error;
        insertedMsg = legacyInsert.data;
      }
      if (msgErr) throw msgErr;

      // Notificação in-app para o comprador
      const { error: notifErr } = await supabase.from("notifications").insert({
        user_id: selectedMessage.sender_id,
        type: "producer_reply",
        title: `Resposta: ${selectedMessage.subject}`,
        body: replyText.trim(),
        data: {
          producer_id: user.id,
          producer_name: profile?.full_name || "Produtor",
          original_message_id: selectedMessage.id,
          event_id: selectedMessage.event_id || null,
        },
      });
      if (notifErr) {
        console.warn("[ProducerInbox] Falha ao criar notificação in-app:", notifErr.message);
      }

      // E-mail via Edge Function
      if (insertedMsg) {
        await supabase.functions
          .invoke("send-message-notification", {
            body: {
              messageId: insertedMsg.id,
              type: "producer_reply",
              originalMessageId: selectedMessage.id,
            },
          })
          .catch((err) =>
            console.warn("[ProducerInbox] Falha ao disparar e-mail:", err),
          );
      }

      const replyTimestamp = new Date().toISOString();
      const { error: updateErr } = await supabase
        .from("producer_messages")
        .update({ replied_at: replyTimestamp })
        .eq("id", selectedMessage.id);

      if (updateErr && /replied_at/i.test(updateErr.message || "")) {
        setLocalRepliedIds((prev) => ({ ...prev, [selectedMessage.id]: true }));
      } else if (updateErr) {
        throw updateErr;
      } else {
        setSelectedMessage((prev: any) =>
          prev?.id === selectedMessage.id
            ? { ...prev, replied_at: replyTimestamp }
            : prev,
        );
      }
    },
    onSuccess: () => {
      toast({ title: t("inbox.toasts.replySuccess") });
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["producer-inbox"] });
    },
    onError: (err: any) =>
      toast({
        title: t("inbox.toasts.replyError"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const handleOpen = (msg: any) => {
    setSelectedMessage(msg);
    if (!msg.is_read) {
      markAsRead.mutate(msg.id);
    }
  };

  const unreadCount = messages.filter((m: any) => !m.is_read).length;

  const messagesWithMeta = useMemo(() => {
    // Separa eventuais respostas de chamados de participantes
    const replies = messages.filter(
      (m: any) => m.subject?.toLowerCase().startsWith("re:")
    );
    const tickets = messages.filter(
      (m: any) => !m.subject?.toLowerCase().startsWith("re:")
    );

    const baseList = tickets.length > 0 ? tickets : messages;

    return baseList.map((msg: any) => {
      // Localiza eventual resposta enviada
      const matchingReply = replies.find(
        (r: any) =>
          r.subject?.toLowerCase() === `re: ${msg.subject?.toLowerCase()}` ||
          (r.producer_id === msg.sender_id && r.subject?.toLowerCase().includes(msg.subject?.toLowerCase()))
      );

      const combined = `${msg.subject || ""} ${msg.message || ""}`.toLowerCase();
      const autoUrgent = /(urgente|estorno|erro|cancel|problema|ajuda)/.test(combined);
      const overrideUrgent = urgentOverrides[msg.id];
      const isUrgent =
        typeof overrideUrgent === "boolean"
          ? overrideUrgent
          : typeof msg.is_urgent === "boolean"
            ? msg.is_urgent
            : autoUrgent;

      const isReplied = Boolean(msg.replied_at || localRepliedIds[msg.id] || matchingReply);

      const status =
        isReplied
          ? "answered"
          : isUrgent
            ? "urgent"
            : !msg.is_read
              ? "new"
              : "open";

      return {
        ...msg,
        replyMessage: matchingReply || null,
        isUrgent,
        status,
      };
    });
  }, [messages, urgentOverrides, localRepliedIds]);

  const filteredMessages = useMemo(() => {
    return messagesWithMeta.filter((msg: any) => {
      const matchesFilter = activeFilter === "all" || msg.status === activeFilter;
      const eventKey = msg.event_id || "none";
      const matchesEvent = activeEventFilter === "all" || eventKey === activeEventFilter;
      const matchesSearch =
        searchTerm.trim().length === 0 ||
        msg.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.event?.title?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesEvent && matchesSearch;
    });
  }, [messagesWithMeta, activeFilter, activeEventFilter, searchTerm]);

  // Sincroniza somente quando a lista ou o modo de tela mudar. Incluir a
  // seleção nas dependências recria o objeto sem parar e bloqueia navegação.
  useEffect(() => {
    setSelectedMessage((previous) => {
      if (!filteredMessages.length) return isMobile ? previous : null;
      if (!previous) return isMobile ? null : filteredMessages[0];

      const stillVisible = filteredMessages.find((msg) => msg.id === previous.id);
      if (stillVisible) return stillVisible;
      return isMobile ? previous : filteredMessages[0];
    });
  }, [filteredMessages, isMobile]);

  const counts = useMemo(() => {
    const all = messagesWithMeta.length;
    const newCount = messagesWithMeta.filter((msg: any) => msg.status === "new").length;
    const urgentCount = messagesWithMeta.filter((msg: any) => msg.status === "urgent").length;
    const answeredCount = messagesWithMeta.filter((msg: any) => msg.status === "answered").length;

    return { all, newCount, urgentCount, answeredCount };
  }, [messagesWithMeta]);

  const eventFilters = useMemo(() => {
    const grouped = new Map<string, { id: string; label: string; count: number }>();

    // Pré-popula com todos os eventos do contexto ativo (da organização ou pessoal)
    (contextEvents || []).forEach((ev: any) => {
      grouped.set(ev.id, { id: ev.id, label: ev.title, count: 0 });
    });

    messagesWithMeta.forEach((msg: any) => {
      const key = msg.event_id || "none";
      const label = msg.event?.title || t("inbox.noEvent");
      const prev = grouped.get(key);
      if (prev) {
        prev.count += 1;
      } else {
        grouped.set(key, { id: key, label, count: 1 });
      }
    });

    return Array.from(grouped.values()).sort((a, b) => b.count - a.count);
  }, [contextEvents, messagesWithMeta, t]);

  // Gerenciamento de templates
  const handleTemplateSubmit = () => {
    const trimmed = templateDraft.trim();
    if (!trimmed) return;

    if (editingTemplateIndex !== null) {
      setQuickReplies((prev) =>
        prev.map((reply, idx) => (idx === editingTemplateIndex ? trimmed : reply)),
      );
      setEditingTemplateIndex(null);
      toast({ title: t("inbox.toasts.templateUpdated") });
    } else {
      setQuickReplies((prev) => [...prev, trimmed]);
      toast({ title: t("inbox.toasts.templateAdded") });
    }

    setTemplateDraft("");
  };

  const handleTemplateEdit = (index: number) => {
    setTemplateDraft(quickReplies[index]);
    setEditingTemplateIndex(index);
  };

  const handleTemplateDelete = (index: number) => {
    setQuickReplies((prev) => prev.filter((_, idx) => idx !== index));
    if (editingTemplateIndex === index) {
      setEditingTemplateIndex(null);
      setTemplateDraft("");
    }
    toast({ title: t("inbox.toasts.templateRemoved") });
  };

  const handleInsertTemplate = (text: string) => {
    setReplyText((prev) => (prev ? `${prev}\n\n${text}` : text));
    toast({ title: t("inbox.useTemplate") });
  };

  const enter = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, transform: "translateY(8px) scale(0.99)" },
          animate: { opacity: 1, transform: "translateY(0) scale(1)" },
          transition: { duration: 0.35, delay: i * 0.04, ease: EASE },
        };

  return (
    <>
      <SEOHead title={t("inbox.pageTitle")} />

      <div className={panelPage}>
        {/* CABEÇALHO ABERTO (Padrão DESIGN.md - sem caixa de Card) */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={panelTitle}>{t("inbox.header")}</h1>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("inbox.headerDesc")}</span>
              {unreadCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  <span className={panelAccentDot} />
                  {unreadCount} {t("inbox.unreadBadge", { count: unreadCount })}
                </span>
              )}
            </div>
            {inboxError && (
              <p className="mt-1.5 text-xs text-destructive">
                {t("inbox.loadError")}: {(inboxError as Error).message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setTemplatesModalOpen(true)}
              className={cn("h-9 gap-1.5 px-3.5 text-xs rounded-full", panelPill)}
            >
              <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{t("inbox.templatesTitle")}</span>
            </Button>
          </div>
        </header>

        {/* 4 CARDS DE ESTATÍSTICA / FILTRO RÁPIDO (Superfícies de 28px) */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              key: "all",
              label: t("inbox.filters.inbox"),
              count: counts.all,
              accent: null,
            },
            {
              key: "new",
              label: t("inbox.filters.unread"),
              count: counts.newCount,
              accent: "new",
            },
            {
              key: "urgent",
              label: t("inbox.filters.urgent"),
              count: counts.urgentCount,
              accent: "urgent",
            },
            {
              key: "answered",
              label: t("inbox.filters.answered"),
              count: counts.answeredCount,
              accent: "answered",
            },
          ].map((item, i) => {
            const isActive = activeFilter === item.key;
            return (
              <motion.button
                key={item.key}
                type="button"
                {...enter(i)}
                onClick={() => setActiveFilter(item.key as typeof activeFilter)}
                className={cn(
                  "relative flex flex-col justify-between rounded-[28px] border p-5 text-left transition-all duration-150 ease-out active:scale-[0.98]",
                  isActive
                    ? "border-primary/40 bg-card ring-1 ring-primary/25 shadow-sm"
                    : "border-border/60 bg-card hover:border-border/80 hover:bg-secondary/20",
                )}
              >
                <div className="flex items-center justify-between">
                  <p className={panelEyebrow}>{item.label}</p>
                  {item.accent === "new" && item.count > 0 && (
                    <span className={panelAccentDot} />
                  )}
                  {item.accent === "urgent" && item.count > 0 && (
                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                  )}
                  {item.accent === "answered" && item.count > 0 && (
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  )}
                </div>
                <p className="mt-2 font-display text-3xl font-semibold tabular-nums tracking-tight text-foreground">
                  {item.count}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* WORKSPACE MASTER-DETAIL DE 2 COLUNAS */}
        {isLoading ? (
          <div className="grid min-h-[560px] gap-5 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[420px_minmax(0,1fr)]">
            <div className={cn(panelSurface, "p-4 sm:p-5 space-y-3")}>
              <div className="h-10 animate-pulse rounded-full bg-secondary/70" />
              <div className="h-11 animate-pulse rounded-2xl bg-secondary/50" />
              <div className="space-y-2 pt-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded-[22px] bg-secondary/40" />
                ))}
              </div>
            </div>
            <div className={cn(panelSurface, "p-6 sm:p-7 hidden lg:block animate-pulse bg-secondary/20")} />
          </div>
        ) : messages.length === 0 ? (
          <div className={cn(panelEmpty, "p-12 text-center")}>
            <Inbox className="mx-auto h-12 w-12 text-muted-foreground/40 mb-3" />
            <h3 className="font-display text-lg font-semibold text-foreground">
              {t("inbox.emptyTitle")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
              {t("inbox.emptyDesc")}
            </p>
          </div>
        ) : (
          <div className="grid min-h-[580px] gap-5 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[420px_minmax(0,1fr)]">
            {/* COLUNA ESQUERDA: LISTA MESTRE (Oculta em mobile se uma conversa estiver selecionada) */}
            <div
              className={cn(
                panelSurface,
                "flex flex-col p-4 sm:p-5 space-y-3.5",
                isMobile && selectedMessage ? "hidden" : "flex",
              )}
            >
              {/* FILTROS EM PÍLULA (Segmented) */}
              <div className="grid grid-cols-4 gap-1 rounded-full bg-secondary/70 p-1">
                {[
                  { key: "all", label: t("inbox.filters.inbox"), count: counts.all },
                  { key: "new", label: t("inbox.filters.unread"), count: counts.newCount },
                  { key: "urgent", label: t("inbox.filters.urgent"), count: counts.urgentCount },
                  { key: "answered", label: t("inbox.filters.answered"), count: counts.answeredCount },
                ].map((f) => {
                  const isActive = activeFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setActiveFilter(f.key as typeof activeFilter)}
                      className={cn(
                        "flex items-center justify-center gap-1 rounded-full py-1.5 px-1 text-center text-[11px] font-medium transition-[background-color,color] duration-150 ease-out truncate",
                        isActive
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <span className="truncate">{f.label}</span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">({f.count})</span>
                    </button>
                  );
                })}
              </div>

              {/* SELETOR DE EVENTOS (Well suave) */}
              {eventFilters.length > 0 && (
                <div>
                  <Select value={activeEventFilter} onValueChange={setActiveEventFilter}>
                    <SelectTrigger className={cn(panelControl, "mt-0 h-11 text-xs")}>
                      <SelectValue placeholder={t("inbox.allEvents")} />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-border/60">
                      <SelectItem value="all" className="rounded-xl text-xs">
                        {t("inbox.allEvents")}
                      </SelectItem>
                      {eventFilters.map((ev) => (
                        <SelectItem key={ev.id} value={ev.id} className="rounded-xl text-xs">
                          {ev.label} ({ev.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* CAMPO DE BUSCA (Well suave) */}
              <SearchInput
                placeholder={t("inbox.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-0 h-11 rounded-2xl border-transparent bg-secondary/50 text-xs font-medium focus-visible:bg-background"
              />

              {/* LISTA SCROLLÁVEL DE CHAMADOS */}
              <div className="flex-1 space-y-2 overflow-y-auto pr-0.5 max-h-[58vh]">
                {filteredMessages.length === 0 ? (
                  <div className={cn(panelEmpty, "p-8 text-center text-xs text-muted-foreground")}>
                    {t("inbox.noResults")}
                  </div>
                ) : (
                  filteredMessages.map((msg: any) => {
                    const isSelected = selectedMessage?.id === msg.id;
                    const initial = msg.sender_name ? msg.sender_name.charAt(0).toUpperCase() : "U";

                    return (
                      <button
                        key={msg.id}
                        type="button"
                        onClick={() => handleOpen(msg)}
                        className={cn(
                          "relative flex w-full cursor-pointer select-none items-start gap-3 rounded-[22px] border p-3.5 text-left transition-all duration-150 ease-out active:scale-[0.98]",
                          isSelected
                            ? "border-primary/40 bg-secondary/80 text-foreground ring-1 ring-primary/25 shadow-sm"
                            : "border-border/60 bg-card hover:border-border hover:bg-secondary/40 text-muted-foreground",
                        )}
                      >
                        {/* Avatar Neutro */}
                        <div className="h-9 w-9 rounded-full bg-secondary text-foreground font-semibold flex items-center justify-center text-xs shrink-0 mt-0.5">
                          {initial}
                        </div>

                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate text-xs font-semibold text-foreground flex items-center gap-1.5">
                              {msg.sender_name}
                              {msg.status === "new" && (
                                <span className={panelAccentDot} title={t("inbox.badgeNew")} />
                              )}
                            </span>
                            <span className="shrink-0 text-[10px] text-muted-foreground">
                              {formatDistanceToNow(new Date(msg.created_at), {
                                addSuffix: false,
                                locale: ptBR,
                              })}
                            </span>
                          </div>

                          <p className="truncate text-xs font-medium text-foreground/90">
                            {msg.subject}
                          </p>
                          <p className="line-clamp-2 break-words text-[11px] text-muted-foreground/80 leading-relaxed">
                            {msg.message}
                          </p>

                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            {msg.event?.title && (
                              <span className="rounded-full bg-secondary/80 px-2 py-0.5 text-[9px] font-medium text-muted-foreground max-w-[150px] truncate">
                                {msg.event.title}
                              </span>
                            )}
                            {msg.isUrgent && (
                              <span className="rounded-full bg-rose-500/10 px-2 py-0.5 text-[9px] font-medium text-rose-600 dark:text-rose-400">
                                {t("inbox.badgeUrgent")}
                              </span>
                            )}
                            {msg.status === "answered" && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-medium text-emerald-600 dark:text-emerald-400">
                                <Check className="h-2.5 w-2.5" />
                                {t("inbox.badgeAnswered")}
                              </span>
                            )}
                            {!msg.is_read ? (
                              <Mail className="h-3 w-3 text-primary ml-auto shrink-0" />
                            ) : (
                              <MailOpen className="h-3 w-3 text-muted-foreground/50 ml-auto shrink-0" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* COLUNA DIREITA: DETALHE DO CHAMADO & COMPOSER (Oculta em mobile se nenhuma conversa selecionada) */}
            <div
              className={cn(
                panelSurface,
                "flex flex-col p-6 sm:p-7 min-h-[580px]",
                isMobile && !selectedMessage ? "hidden" : "flex",
              )}
            >
              {!selectedMessage ? (
                <div className="my-auto flex flex-1 flex-col items-center justify-center space-y-3 p-6 text-center text-muted-foreground">
                  <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-1" />
                  <p className="font-display text-base font-semibold text-foreground">
                    {t("inbox.selectConversation")}
                  </p>
                  <p className="max-w-xs text-xs text-muted-foreground">
                    {t("inbox.headerDesc")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-1 flex-col space-y-5">
                  {/* CABEÇALHO DO CHAMADO SELECIONADO */}
                  <div className="space-y-3 border-b border-border/60 pb-4">
                    {isMobile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedMessage(null)}
                        className={cn("h-8 gap-1.5 px-2.5 -ml-2 text-xs font-medium rounded-full mb-1", panelPill)}
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        <span>Voltar para mensagens</span>
                      </Button>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground break-words">
                          {selectedMessage.subject}
                        </h2>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {selectedMessage.sender_name}
                          </span>
                          <span>•</span>
                          <span className="text-muted-foreground">{selectedMessage.sender_email}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {new Date(selectedMessage.created_at).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              timeZone: "America/Sao_Paulo",
                            })}
                          </span>
                        </div>
                      </div>

                      {/* AÇÕES DE CABEÇALHO */}
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            toggleUrgency.mutate({
                              messageId: selectedMessage.id,
                              value: !selectedMessage.isUrgent,
                            })
                          }
                          disabled={toggleUrgency.isPending}
                          className={cn(
                            "h-8 gap-1.5 px-3 text-xs rounded-full",
                            panelPill,
                            selectedMessage.isUrgent &&
                              "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
                          )}
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {selectedMessage.isUrgent
                            ? t("inbox.removeUrgency")
                            : t("inbox.markUrgent")}
                        </Button>

                        {selectedMessage.event_id && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className={cn("h-8 gap-1.5 px-3 text-xs rounded-full", panelPill)}
                          >
                            <Link
                              to={`/produtor/eventos/${selectedMessage.event_id}/painel/mensagens`}
                            >
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="hidden sm:inline">{t("inbox.openEventMessages")}</span>
                              <span className="sm:hidden">Painel do evento</span>
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* BALÃO DA MENSAGEM DO PARTICIPANTE */}
                  <div className="flex-1 space-y-3 overflow-y-auto pr-1">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
                        <span className="font-medium text-foreground">{selectedMessage.sender_name}</span>
                        {selectedMessage.event?.title && (
                          <span className="rounded-full bg-secondary/70 px-2 py-0.5 text-[10px]">
                            {selectedMessage.event.title}
                          </span>
                        )}
                      </div>
                      <div className="rounded-2xl bg-secondary/50 p-5 text-sm leading-relaxed text-foreground select-text whitespace-pre-wrap">
                        {selectedMessage.message}
                      </div>
                    </div>

                    {selectedMessage.replyMessage && (
                      <div className="space-y-1.5 pl-3 border-l-2 border-primary/40">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
                          <span className="font-medium text-primary flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            {selectedMessage.replyMessage.sender_name || "Sua resposta"}
                          </span>
                          <span>
                            {new Date(selectedMessage.replyMessage.created_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                          </span>
                        </div>
                        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-sm leading-relaxed text-foreground select-text whitespace-pre-wrap">
                          {selectedMessage.replyMessage.message}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* FORMULÁRIO DE RESPOSTA */}
                  <div className="mt-auto space-y-3 border-t border-border/60 pt-4">
                    {/* BARRA DE MODELOS RÁPIDOS */}
                    {quickReplies.length > 0 && (
                      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
                        <span className="text-[11px] font-medium text-muted-foreground shrink-0 mr-1">
                          Modelos rápidos:
                        </span>
                        {quickReplies.slice(0, 3).map((template, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleInsertTemplate(template)}
                            className="shrink-0 rounded-full border border-border/60 bg-secondary/40 px-3 py-1 text-[11px] text-muted-foreground hover:bg-secondary/80 hover:text-foreground transition-colors duration-150 ease-out"
                            title={template}
                          >
                            {template.length > 32 ? template.slice(0, 32) + "…" : template}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setTemplatesModalOpen(true)}
                          className="shrink-0 rounded-full border border-dashed border-border/70 px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Ver todos ({quickReplies.length})
                        </button>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className={panelFieldLabel}>{t("inbox.replyLabel")}</label>
                        <span className="text-[11px] text-muted-foreground">
                          {replyText.length}/2000 · Ctrl+Enter envia
                        </span>
                      </div>
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && replyText.trim() && !replyMutation.isPending) {
                            e.preventDefault();
                            replyMutation.mutate();
                          }
                        }}
                        placeholder={t("inbox.replyPlaceholder")}
                        rows={4}
                        maxLength={2000}
                        className={cn(panelControl, "h-auto min-h-[110px] py-3 text-sm leading-relaxed resize-none")}
                      />
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplyText("")}
                        disabled={!replyText || replyMutation.isPending}
                        className={cn("h-9 rounded-full text-muted-foreground hover:text-foreground text-xs", panelPill)}
                      >
                        {t("inbox.clearButton")}
                      </Button>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() => replyMutation.mutate()}
                        disabled={!replyText.trim() || replyMutation.isPending}
                        className={cn("h-9 gap-1.5 px-5 rounded-full text-xs font-medium", panelPill)}
                      >
                        {replyMutation.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                        <span>{replyMutation.isPending ? t("inbox.sendingReply") : t("inbox.sendReply")}</span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL DE GERENCIAMENTO DE MODELOS DE RESPOSTA (100% DESIGN.md & Dark Mode) */}
      <Dialog open={templatesModalOpen} onOpenChange={setTemplatesModalOpen}>
        <DialogContent mobilePresentation="fullscreen" className={cn(panelDialog, "sm:max-w-lg p-6")}>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="font-display text-lg font-semibold tracking-tight text-foreground">
              {t("inbox.templatesTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {t("inbox.templatesDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* NOVO / EDITAR MODELO */}
            <div className="space-y-2">
              <label className={panelFieldLabel}>
                {editingTemplateIndex !== null ? "Editar modelo" : t("inbox.addTemplate")}
              </label>
              <div className="flex gap-2">
                <Textarea
                  value={templateDraft}
                  onChange={(e) => setTemplateDraft(e.target.value)}
                  placeholder={t("inbox.replyPlaceholder")}
                  rows={2}
                  className={cn(panelControl, "h-auto py-2.5 text-xs resize-none")}
                />
              </div>
              <div className="flex justify-end gap-2">
                {editingTemplateIndex !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn("h-8 rounded-full text-xs", panelPill)}
                    onClick={() => {
                      setEditingTemplateIndex(null);
                      setTemplateDraft("");
                    }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={handleTemplateSubmit}
                  disabled={!templateDraft.trim()}
                  className={cn("h-8 rounded-full text-xs font-medium px-4", panelPill)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  {editingTemplateIndex !== null
                    ? t("inbox.saveTemplate")
                    : t("inbox.addTemplate")}
                </Button>
              </div>
            </div>

            {/* LISTA DE MODELOS EXISTENTES */}
            <div className="space-y-2 pt-2 border-t border-border/60">
              <p className="text-xs font-medium text-muted-foreground">
                {t("inbox.templatesLabel")} ({quickReplies.length})
              </p>
              <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
                {quickReplies.map((template, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      panelRow,
                      "flex items-start justify-between gap-3 p-3.5 text-left transition-colors hover:bg-secondary/30",
                    )}
                  >
                    <p className="text-xs leading-relaxed text-foreground flex-1 select-text">
                      {template}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => handleTemplateEdit(idx)}
                        className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground"
                        title="Editar modelo"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => handleTemplateDelete(idx)}
                        className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive"
                        title="Excluir modelo"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          handleInsertTemplate(template);
                          setTemplatesModalOpen(false);
                        }}
                        className={cn("h-7 rounded-full text-[11px] px-2.5 ml-1", panelPill)}
                      >
                        Usar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
